import toast, { type ToastOptions } from 'react-hot-toast';
import {
  CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon, InformationCircleIcon,
} from '@/icons';

/**
 * Themed wrapper over react-hot-toast so every transient notification looks
 * consistent (icon + tone) without repeating styling at call sites. The base
 * toast styling (surface/border/fg) is set globally in AppProviders.
 */
const base: ToastOptions = { duration: 4000 };
// Screen-reader announcements: routine toasts are polite; failures assert.
const polite: ToastOptions['ariaProps'] = { role: 'status', 'aria-live': 'polite' };
const assertive: ToastOptions['ariaProps'] = { role: 'alert', 'aria-live': 'assertive' };

export const notify = {
  success: (message: string, opts?: ToastOptions) =>
    toast.success(message, { icon: <CheckCircleIcon className="h-5 w-5 text-success" />, ariaProps: polite, ...base, ...opts }),
  error: (message: string, opts?: ToastOptions) =>
    toast.error(message, { icon: <XCircleIcon className="h-5 w-5 text-danger" />, ariaProps: assertive, duration: 6000, ...opts }),
  warning: (message: string, opts?: ToastOptions) =>
    toast(message, { icon: <ExclamationTriangleIcon className="h-5 w-5 text-warning" />, ariaProps: assertive, ...base, ...opts }),
  info: (message: string, opts?: ToastOptions) =>
    toast(message, { icon: <InformationCircleIcon className="h-5 w-5 text-info" />, ariaProps: polite, ...base, ...opts }),
  loading: (message: string, opts?: ToastOptions) => toast.loading(message, opts),
  dismiss: (id?: string) => toast.dismiss(id),
  /** Drive success/error toasts from a promise's lifecycle. */
  promise: <T,>(p: Promise<T>, msgs: { loading: string; success: string; error: string }) =>
    toast.promise(p, msgs),
};
